import React, { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import styles from "./styles/Demo.module.css";
import { useGetDiscordLoginUrlMutation } from "@features/DiscordAuth/api/authApiSlice";
import { useGetFaceitLoginUrlMutation } from "@entities/Faceit/FaceitloginApi";
import { useSelector, useDispatch } from "react-redux";
import { selectIsAuthenticated } from "@features/DiscordAuth/model/selectors";
import {
  useGetFirstSkinQuery,
  useGetOnboardingProgressQuery,
} from "@entities/Onboarding/Boardingapi";
import { addToast } from "@features/toasts/toastSlice";
import { selectTradeLink } from "@entities/User/model/slice";
import { Modal } from "@shared/ui/Modal/Modal";
import { useSubmitTradeLinkMutation } from "@entities/User/api/UserInformationSlice";
import { validateTradeLink } from "@shared/lib/validateTradeLink";

import useWindowWidth from "@shared/hooks/useWindowWidth"; // Импортируем хук

const MOBILE_BREAKPOINT = 768; // Определяем breakpoint

const DemoPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showElements, setShowElements] = useState(false);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [getDiscordLoginUrl] = useGetDiscordLoginUrlMutation();
  const [getFaceitLoginUrl] = useGetFaceitLoginUrlMutation();
  const dispatch = useDispatch();
  const tradeLink = useSelector(selectTradeLink);
  const [isTradeLinkModalOpen, setIsTradeLinkModalOpen] = useState(false);
  const [tradeLinkInput, setTradeLinkInput] = useState("");
  const [submitTradeLink, { isLoading }] = useSubmitTradeLinkMutation();
  const windowWidth = useWindowWidth();

  const {
    data: firstSkinData,
    isLoading: isFirstSkinLoading,
    refetch,
  } = useGetFirstSkinQuery(undefined, { skip: true });
  
  const { data: onboardingProgress } = useGetOnboardingProgressQuery();

  const handleLogin = async () => {
    try {
      const response = await getDiscordLoginUrl().unwrap();
      window.location.href = response.url;
    } catch (error) {
      console.error("Ошибка получения URL для авторизации:", error);
    }
  };

  const handleFaceitLogin = async () => {
    try {
      const response = await getFaceitLoginUrl().unwrap();
      window.location.href = response.url;
    } catch (error) {
      console.error("Ошибка получения URL для авторизации Faceit:", error);
    }
  };

  // Массив с путями к PNG изображениям
  const slides = [
    "/landing/slide2.png",
    "/landing/slide3.png",
    "/landing/slide4.png",
    "/landing/slide5.png",
    "/landing/slide6.png",
  ];

  const handleGoBack = () => {
    navigate({ to: "/" });
  };

  const goToNextSlide = () => {
    setShowElements(false);

    // Задержка перед сменой слайда для анимации исчезновения элементов
    setTimeout(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
    }, 300);
  };

  const goToPrevSlide = () => {
    setShowElements(false);

    // Задержка перед сменой слайда для анимации исчезновения элементов
    setTimeout(() => {
      setCurrentSlide((prevSlide) =>
        prevSlide === 0 ? slides.length - 1 : prevSlide - 1
      );
    }, 300);
  };

  // Показываем элементы после загрузки нового слайда
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowElements(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentSlide]);

  const handleGetSkin = async () => {
    if (!tradeLink) {
      setIsTradeLinkModalOpen(true);
      return;
    }

    try {
      const result = await refetch();
      console.log("result", result);
      if (result.error) {
        const errorMessage =
          (result.error as any)?.data?.Error ||
          "Произошла ошибка при получении скина";
        if (errorMessage.includes("User has not played a match in the clan")) {
          dispatch(
            addToast({
              title: "Ошибка",
              description: "Вы не сыграли ни одного матча в клане",
              type: "error",
            })
          );
        } else if (
          errorMessage.includes("User has played more than 1 match in the clan")
        ) {
          dispatch(
            addToast({
              title: "Ошибка",
              description: "Вы сыграли больше одного матча в клане",
              type: "error",
            })
          );
        } else if (errorMessage.includes("Trade link not provided")) {
          dispatch(
            addToast({
              title: "Ошибка",
              description: "Не указан трейд-линк",
              type: "error",
            })
          );
        } else if (errorMessage.includes("Failed to send trade offer")) {
          dispatch(
            addToast({
              title: "Ошибка",
              description:
                "Не удалось отправить трейд-оффер. Проверьте, что трейд-линк не приватный. Если проблема сохраняется, обратитесь в поддержку.",
              type: "error",
            })
          );
        }
        return;
      }

      if (firstSkinData?.product) {
        console.log("Получение скина:", firstSkinData.product);
        dispatch(
          addToast({
            title: "Успех",
            description: "Скин успешно получен!",
            type: "success",
          })
        );
      }
      handleGoBack();
    } catch (error) {
      console.error("Ошибка при получении скина:", error);
      dispatch(
        addToast({
          title: "Ошибка",
          description: "Произошла неизвестная ошибка при получении скина",
          type: "error",
        })
      );
    }
  };

  const handleTradeLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateTradeLink(tradeLinkInput);
    if (validationError) {
      dispatch(
        addToast({
          title: "Ошибка",
          description: validationError,
          type: "error",
        })
      );
      return;
    }

    try {
      const response = await submitTradeLink(tradeLinkInput).unwrap();
      if (response.success) {
        setIsTradeLinkModalOpen(false);
        setTradeLinkInput("");
        handleGetSkin();
      } else {
        dispatch(
          addToast({
            title: "Ошибка",
            description:
              response.message || "Ошибка при сохранении трейд-линка",
            type: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        addToast({
          title: "Ошибка",
          description: "Произошла ошибка при сохранении трейд-линка",
          type: "error",
        })
      );
    }
  };

  useEffect(() => {
    if (windowWidth < MOBILE_BREAKPOINT) {
      navigate({ to: "/demo-mobile" });
    }
  }, [windowWidth]);

  // Рендер элементов в зависимости от текущего слайда
  const renderSlideElements = () => {
    switch (currentSlide) {
      case 0: // Первый слайд - две кнопки
        return (
          <>
            <button
              className={`${styles.element} ${styles.button1} ${showElements ? styles.visible : ""}`}
              onClick={() =>
                console.log("Кнопка 1 на слайде 1 - ничего не делает")
              }
            >
              <img
                src="/landing/Premier.svg"
                alt="Кнопка 1"
                width="100%"
                height="100%"
              />
            </button>

            <button
              className={`${styles.element} ${styles.button2} ${showElements ? styles.visible : ""}`}
              onClick={goToNextSlide}
            >
              <img
                src="/landing/Fesit.svg"
                alt="Кнопка 2"
                width="100%"
                height="100%"
              />
            </button>
          </>
        );

      case 1: // Второй слайд - одна кнопка и две стрелки по центрам
        return (
          <>
            <button
              className={`${styles.element} ${styles.button3} ${showElements ? styles.visible : ""}`}
              onClick={() => handleLogin()}
            >
              <img
                src="/landing/Voiti.svg"
                alt="Кнопка"
                width="100%"
                height="100%"
              />
            </button>
            <button
              className={`${styles.element} ${styles.rightArrow} ${isAuthenticated ? styles.visible : ""}`}
              onClick={goToNextSlide}
            >
              <img
                src="/landing/arrow-right.svg"
                alt="Стрелка вправо"
                width="100%"
                height="100%"
              />
            </button>
          </>
        );

      case 2: // Третий слайд - одна кнопка и две стрелки
        return (
          <>
            <button
              className={`${styles.element} ${styles.centerButton} ${showElements ? styles.visible : ""}`}
              onClick={handleFaceitLogin}
            >
              <img
                src="/landing/Fesit.svg"
                alt="Кнопка"
                width="100%"
                height="100%"
              />
            </button>

            <button
              className={`${styles.element} ${styles.leftArrow} ${showElements ? styles.visible : ""}`}
              onClick={goToPrevSlide}
            >
              <img
                src="/landing/arrow-left.svg"
                alt="Стрелка влево"
                width="100%"
                height="100%"
              />
            </button>

            <button
              className={`${styles.element} ${styles.rightArrow} ${onboardingProgress?.hasFaceitId ? styles.visible : ""}`} 
              onClick={goToNextSlide}
            >
              <img
                src="/landing/arrow-right.svg"
                alt="Стрелка вправо"
                width="100%"
                height="100%"
              />
            </button>
          </>
        );

      case 3:
        return (
          <>
            <button
              className={`${styles.element} ${styles.button4} ${showElements ? styles.visible : ""}`}
              onClick={() =>
                window.open("https://www.faceit.com/ru/inv/xxvwk5i", "_blank")
              }
            >
              <img
                src="/landing/OpenCS.svg"
                alt="Кнопка"
                width="100%"
                height="100%"
              />
            </button>

            <button
              className={`${styles.element} ${styles.leftArrow} ${showElements ? styles.visible : ""}`}
              onClick={goToPrevSlide}
            >
              <img
                src="/landing/arrow-left.svg"
                alt="Стрелка влево"
                width="100%"
                height="100%"
              />
            </button>
            <button
              className={`${styles.element} ${styles.rightArrow} ${onboardingProgress?.isClanMember ? styles.visible : ""}`}
              onClick={goToNextSlide}
            >
              <img
                src="/landing/arrow-right.svg"
                alt="Стрелка вправо"
                width="100%"
                height="100%"
              />
            </button>
          </>
        );

      case 4: // Пятый слайд - кнопка и обе стрелки
        return (
          <>
            <button
              className={`${styles.element} ${styles.homeButton} ${showElements ? styles.visible : ""}`}
              onClick={handleGetSkin}
              disabled={isFirstSkinLoading}
            >
              <img
                src="/landing/GetSkin.svg"
                alt="На главную"
                width="100%"
                height="100%"
              />
            </button>

            <button
              className={`${styles.element} ${styles.leftArrow} ${showElements ? styles.visible : ""}`}
              onClick={goToPrevSlide}
            >
              <img
                src="/landing/arrow-left.svg"
                alt="Стрелка влево"
                width="100%"
                height="100%"
              />
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.demoPage}>
      <div className={styles.container}>
        <img
          src={slides[currentSlide]}
          alt={`Фоновое изображение ${currentSlide + 1}`}
          className={styles.fullscreenImage}
        />

        {/* Рендер элементов в зависимости от текущего слайда */}
        {renderSlideElements()}

        <button className={styles.floatingButton} onClick={handleGoBack}>
          <img
            src="/landing/Close.svg"
            alt="Кнопка"
            width="100%"
            height="100%"
          />
        </button>
      </div>

      <Modal
        isOpen={isTradeLinkModalOpen}
        onClose={() => setIsTradeLinkModalOpen(false)}
      >
        <div className={styles.tradeLinkModal}>
          <button
            className={styles.closeButton}
            onClick={() => setIsTradeLinkModalOpen(false)}
          >
            <img src="/landing/Close.svg" alt="Закрыть" />
          </button>
          <div className={styles.tradeLinkContent}>
            <form onSubmit={handleTradeLinkSubmit}>
              <input
                type="text"
                placeholder="HTTPS://STORE.STEAM/MYTRADELINK"
                value={tradeLinkInput}
                onChange={(e) => setTradeLinkInput(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className={styles.submitButton}
              >
                <img src="/landing/Push.svg" alt="Отправить" />
              </button>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DemoPage;
